import type { ReactElement } from 'react'

import { Navigate, createBrowserRouter, useLocation } from 'react-router'

import { PlaceholderPage } from '~/components/common/PlaceholderPage'
import { ErrorLayout } from '~/components/layouts/ErrorLayout'
import { ProtectedMainLayout } from '~/components/layouts/MainLayout'
import { AdminApprovalFlowPage } from '~/pages/AdminApprovalFlow'
import { AdminApprovalTasksPage } from '~/pages/AdminApprovalTasks'
import { AdminBidLimitsPage } from '~/pages/AdminBidLimits'
import { AdminHubPage } from '~/pages/AdminHub'
import { AdminJepxMemberInfoPage } from '~/pages/AdminJepxMemberInfo'
import { AdminLossRatePage } from '~/pages/AdminLossRate'
import { AdminOcctoMasterPage } from '~/pages/AdminOcctoMaster'
import { AdminSingularDayPage } from '~/pages/AdminSingularDay'
import { AdminUploadHistoryPage } from '~/pages/AdminUploadHistory'
import { AdminUploadsPage } from '~/pages/AdminUploads'
import { AdminWeatherForecastPointsPage } from '~/pages/AdminWeatherForecastPoints'
import { AdminWorkflowTemplatesPage } from '~/pages/AdminWorkflowTemplates'
import { AnnouncementDetailPage } from '~/pages/AnnouncementDetail'
import { AnnouncementsPage } from '~/pages/Announcements'
import { BalanceDemandPage } from '~/pages/BalanceDemand'
import { BalanceDemandDetailPage } from '~/pages/BalanceDemandDetail'
import { BalanceGenerationPage } from '~/pages/BalanceGeneration'
import { BalanceGenerationDetailPage } from '~/pages/BalanceGenerationDetail'
import { BalanceImputationPolicyPage } from '~/pages/BalanceImputationPolicy'
import { BilateralTradeCsvUploadPage } from '~/pages/BilateralTradeCsvUpload'
import { CisContractCsvUploadPage } from '~/pages/CisContractCsvUpload'
import { CustomerContractCsvUploadPage } from '~/pages/CustomerContractCsvUpload'
import { CustomerInfoPage } from '~/pages/CustomerInfo'
import { DemandForecastDailyPage } from '~/pages/DemandForecastDaily'
import { DemandForecastListPage } from '~/pages/DemandForecastList'
import { DemandGroupCsvUploadPage } from '~/pages/DemandGroupCsvUpload'
import { DemandSettingsPage } from '~/pages/DemandSettings'
import { GenerationSalesPlanDetailPage } from '~/pages/GenerationSalesPlanDetail'
import { IntradayBiddingPage } from '~/pages/IntradayBidding'
import { IntradayCsvImportPage } from '~/pages/IntradayCsvImport'
import { JepxSettlementPage } from '~/pages/JepxSettlement'
import { ProcurementPlanDetailPage } from '~/pages/ProcurementPlanDetail'
import { ProcurementPlanNoSubmissionRecordPage } from '~/pages/ProcurementPlanDetail/NoSubmissionRecord'
import { ProcurementPlanListPage } from '~/pages/ProcurementPlanList'
import { SettingsPage } from '~/pages/Settings'
import { SpotBiddingPage } from '~/pages/SpotBidding'
import { SpotCsvImportPage } from '~/pages/SpotCsvImport'
import { SpotPricePage } from '~/pages/SpotPrice'
import { SubmissionManagementPage } from '~/pages/SubmissionManagement'
import { Supply48DataPage } from '~/pages/Supply48Data'
import { Supply48DataCsvUploadPage } from '~/pages/Supply48DataCsvUpload'
import { SupplyBilateralPage } from '~/pages/SupplyBilateral'
import { SupplyCapacityChangePage } from '~/pages/SupplyCapacityChange'
import { SupplyGapPage } from '~/pages/SupplyGap'
import { WorkflowCaseDetailPage } from '~/pages/WorkflowCaseDetail'
import { WorkflowCasesPage } from '~/pages/WorkflowCases'
import {
  DEMAND_FORECAST_DAILY_LEGACY_PATH,
  routePaths,
} from '~/routes/routePaths'

/**
 * トップページ (`/`) の着地先。
 *
 * spec: `docs/specs/common/ecloud-landing.md` (AC-CEL-001)。 eCloud ホームからの遷移・
 * 直リンク・ブックマークのいずれでルートに着地しても、 業務の入口である業務ワークフロー
 * 一覧へ寄せる (Phase 1 は全プラン共通で固定 = プランによる出し分けは行わない)。
 *
 * TODO(#3405): 松竹梅プランを実装したら、 着地先はプランによって変更する予定
 * (spec 「段階リリース」 Phase 3 以降: ダッシュボード画面ができた場合に
 * 梅 = 業務ワークフロー一覧 / 竹・松 = ダッシュボード の出し分けを再検討する)。
 */
const homeRedirectPath = routePaths.workflowCases.path

/**
 * 旧 URL `/admin/demand-groups?…` を `/demand/settings?…&tab=demand-groups` へ置き換える。
 * 業務ワークフローの連携ページ (`?workflowId=`) から来た時に元の query を保つ (戻り先の解決に使う)。
 */
const LegacyAdminDemandGroupsRedirect = (): ReactElement => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', 'demand-groups')
  return (
    <Navigate
      replace
      to={{
        pathname: routePaths.demandSettings.path,
        search: `?${params.toString()}`,
      }}
    />
  )
}

/**
 * 実装済みページの routePaths key → element。
 *
 * これを唯一の SoT とし、 placeholder route は「実装済み集合の補集合」として導出する
 * (= 旧実装の `key !== 'xxx'` denylist と children 明示列挙の二重メンテを解消)。
 * 新規ページ実装時はこの map に 1 行追加するだけでよく、 placeholder からの除外漏れが起きない。
 *
 * `home` だけは実ページでなくリダイレクト (= `homeRedirectPath` の JSDoc 参照)。
 */
const implementedPages: Partial<Record<keyof typeof routePaths, ReactElement>> =
  {
    home: <Navigate replace to={homeRedirectPath} />,
    workflowCases: <WorkflowCasesPage />,
    workflowCaseDetail: <WorkflowCaseDetailPage />,
    demandForecastList: <DemandForecastListPage />,
    demandForecastDaily: <DemandForecastDailyPage />,
    supplyBilateral: <SupplyBilateralPage />,
    supplyGap: <SupplyGapPage />,
    supply48Data: <Supply48DataPage />,
    biddingSpot: <SpotBiddingPage />,
    biddingSpotCsvImport: <SpotCsvImportPage />,
    biddingIntraday: <IntradayBiddingPage />,
    biddingIntradayCsvImport: <IntradayCsvImportPage />,
    biddingSupplyCapacityChange: <SupplyCapacityChangePage />,
    biddingSpotPrice: <SpotPricePage />,
    biddingSettlement: <JepxSettlementPage />,
    submissionManagement: <SubmissionManagementPage />,
    planProcurement: <ProcurementPlanListPage />,
    planProcurementDetail: <ProcurementPlanDetailPage />,
    planProcurementNoSubmissionRecord: (
      <ProcurementPlanNoSubmissionRecordPage />
    ),
    planGenSalesDetail: <GenerationSalesPlanDetailPage />,
    balanceDemand: <BalanceDemandPage />,
    balanceDemandDetail: <BalanceDemandDetailPage />,
    balanceGeneration: <BalanceGenerationPage />,
    balanceGenerationDetail: <BalanceGenerationDetailPage />,
    balanceImputationPolicy: <BalanceImputationPolicyPage />,
    customerInfo: <Navigate replace to={routePaths.customerList.path} />,
    customerList: <CustomerInfoPage />,
    customerContractors: <CustomerInfoPage />,
    customerContract: <CustomerInfoPage />,
    customerSchedule: <CustomerInfoPage />,
    adminHub: <AdminHubPage />,
    adminOccto: <AdminOcctoMasterPage />,
    adminLossRate: <AdminLossRatePage />,
    adminWeatherForecastPoints: <AdminWeatherForecastPointsPage />,
    adminWorkflowTemplates: <AdminWorkflowTemplatesPage />,
    adminApprovalFlow: <AdminApprovalFlowPage />,
    adminBidLimits: <AdminBidLimitsPage />,
    adminApprovalTasks: <AdminApprovalTasksPage />,
    adminJepxMemberInfo: <AdminJepxMemberInfoPage />,
    demandSingularDay: <AdminSingularDayPage />,
    adminUploads: <AdminUploadsPage />,
    adminUploadsSupply48Data: <Supply48DataCsvUploadPage />,
    demandSettings: <DemandSettingsPage />,
    // 旧 URL・システム管理ハブのカードの行き先。需要グループは需要想定設定のタブになった
    adminDemandGroups: <LegacyAdminDemandGroupsRedirect />,
    adminUploadsDemandGroups: <DemandGroupCsvUploadPage />,
    adminUploadsBilateralTrade: <BilateralTradeCsvUploadPage />,
    adminUploadsCisContract: <CisContractCsvUploadPage />,
    adminUploadsCustomerContract: <CustomerContractCsvUploadPage />,
    adminUploadHistory: <AdminUploadHistoryPage />,
    settings: <SettingsPage />,
    announcements: <AnnouncementsPage />,
    announcementDetail: <AnnouncementDetailPage />,
  }

/** 旧 URL `/demand/forecast-daily/:section?…` を `/demand/forecast-daily?…` へ置き換える。 */
const LegacyDemandForecastDailyRedirect = (): ReactElement => {
  const { search } = useLocation()
  return (
    <Navigate
      replace
      to={{ pathname: routePaths.demandForecastDaily.path, search }}
    />
  )
}

// 全 routePaths を「実装済みなら実ページ / それ以外は PlaceholderPage」 に展開する。
export const routeChildren = [
  ...Object.entries(routePaths).map(([key, route]) => ({
    path: route.path,
    element: implementedPages[key as keyof typeof routePaths] ?? (
      <PlaceholderPage routeKey={key as keyof typeof routePaths} />
    ),
  })),
  // 旧 URL (断面セグメント付き) は query を保ったまま断面なし URL へ置き換える (AC-DFD-000)。
  // 断面の部分は判定にも API にも使わない。
  {
    path: DEMAND_FORECAST_DAILY_LEGACY_PATH,
    element: <LegacyDemandForecastDailyRedirect />,
  },
]

export const router = createBrowserRouter([
  {
    element: <ProtectedMainLayout />,
    errorElement: <ErrorLayout />,
    children: [
      ...routeChildren,
      {
        path: '*',
        element: <Navigate replace to={routePaths.home.path} />,
      },
    ],
  },
])
